import React, { useState, useMemo } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Star, Heart, MessageCircle, Upload, MoreVertical, Trash2, Download, Wand2, ArrowRight, PlusSquare } from 'lucide-react'
import { Image, Scene } from '../../types'
import { useProjectStore } from '../../stores/projectStore'
import { useAuthStore } from '../../stores/authStore'
import MoveImageDialog from './MoveImageDialog'
import CreateSceneDialog from './CreateSceneDialog'

interface ImageGalleryProps {
  images: Image[]
  selectedImageId: string | null
  selectedScene: Scene | undefined
  viewMode: 'grid' | 'list'
  filterType: 'all' | 'main' | 'favorites' | 'generated' | 'uploaded' | 'comments'
  sortOrder: 'desc' | 'asc'
  thumbnailScale?: number // 0.5 to 1.5, default 1
  onImageSelect: (imageId: string, showComments?: boolean) => void
  onUploadClick: () => void
  onGenerateClick: () => void
}

const ImageGallery: React.FC<ImageGalleryProps> = ({
  images,
  selectedImageId,
  selectedScene,
  viewMode,
  filterType,
  sortOrder,
  thumbnailScale = 1,
  onImageSelect,
  onUploadClick,
  onGenerateClick
}) => {
  const [activeMenuId, setActiveMenuId] = useState<string | null>(null)
  const [moveDialogOpen, setMoveDialogOpen] = useState(false)
  const [createSceneDialogOpen, setCreateSceneDialogOpen] = useState(false)
  const [selectedActionImageId, setSelectedActionImageId] = useState<string | null>(null)
  
  const { setMainImage, toggleFavorite, deleteImage, loading } = useProjectStore()
  const { user } = useAuthStore()

  // Filter and sort images
  const filteredAndSortedImages = useMemo(() => {
    let filtered = [...images]

    // Apply filters
    switch (filterType) {
      case 'main':
        filtered = filtered.filter(img => img.isMain)
        break
      case 'favorites':
        filtered = filtered.filter(img => img.isFavorite)
        break
      case 'generated':
        filtered = filtered.filter(img => img.name.startsWith('ai_'))
        break
      case 'uploaded':
        filtered = filtered.filter(img => !img.name.startsWith('ai_'))
        break
      case 'comments':
        filtered = filtered.filter(img => img.comments.length > 0)
        filtered.sort((a, b) => b.comments.length - a.comments.length)
        break
    }

    // Apply sort by date (if not comments filter which has its own sort)
    if (filterType !== 'comments') {
      filtered.sort((a, b) => {
        const dateA = new Date(a.uploadedAt).getTime()
        const dateB = new Date(b.uploadedAt).getTime()
        return sortOrder === 'desc' ? dateB - dateA : dateA - dateB
      })
    }

    return filtered
  }, [images, filterType, sortOrder])

  const handleMenuClick = (e: React.MouseEvent, imageId: string) => {
    e.stopPropagation()
    setActiveMenuId(activeMenuId === imageId ? null : imageId)
  }

  const handleOpenMoveDialog = (imageId: string) => {
    setSelectedActionImageId(imageId)
    setMoveDialogOpen(true)
    setActiveMenuId(null)
  }

  const handleOpenCreateSceneDialog = (imageId: string) => {
    setSelectedActionImageId(imageId)
    setCreateSceneDialogOpen(true)
    setActiveMenuId(null)
  }

  const handleSetMainImage = async (imageId: string) => {
    if (!selectedScene) return
    try {
      await setMainImage(imageId, selectedScene.id)
      setActiveMenuId(null)
    } catch (error) {
      console.error('Failed to set main image:', error)
    }
  }

  const handleToggleFavorite = async (imageId: string) => {
    try {
      await toggleFavorite(imageId)
    } catch (error) {
      console.error('Failed to toggle favorite:', error)
    }
  }

  const handleDeleteImage = async (imageId: string) => {
    if (window.confirm('Are you sure you want to delete this image?')) {
      try {
        await deleteImage(imageId)
        setActiveMenuId(null)
      } catch (error) {
        console.error('Failed to delete image:', error)
      }
    }
  }

  const getCommentCount = (image: Image) => {
    return image.comments.reduce((total, comment) => total + 1 + comment.replies.length, 0)
  }

  if (!selectedScene) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <Upload className="w-16 h-16 text-white/30 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-white mb-2">No Scene Selected</h3>
          <p className="text-white/70">
            Select a scene from the sidebar to view and manage images
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="h-full flex flex-col">
      {/* Gallery Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {images.length > 0 ? (
          <div 
            className={viewMode === 'grid' ? 'grid gap-4' : 'space-y-4'}
            style={viewMode === 'grid' ? {
              gridTemplateColumns: `repeat(auto-fill, minmax(${Math.round(200 * thumbnailScale)}px, 1fr))`
            } : undefined}
          >
            <AnimatePresence>
              {/* Combined Upload & Generate Placeholder */}
              <motion.div
                key="placeholder-combined"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="group relative glass-card overflow-hidden border-2 border-dashed border-white/20"
              >
                <div className="aspect-w-16 aspect-h-9 bg-gray-800">
                  <div className="absolute inset-0 flex flex-col">
                    {/* Upload Section - Top Half */}
                    <button
                      onClick={onUploadClick}
                      className="flex-1 flex items-center justify-center bg-green-500/10 hover:bg-green-500/15 border-b border-dashed border-green-500/30 transition-colors"
                    >
                      <div className="text-center">
                        <Upload className="w-10 h-10 text-green-400/60 mx-auto mb-2" />
                        <p className="text-white/70 font-medium text-sm">Upload Image</p>
                      </div>
                    </button>
                    
                    {/* Generate Section - Bottom Half */}
                    <button
                      onClick={onGenerateClick}
                      className="flex-1 flex items-center justify-center bg-purple-500/10 hover:bg-purple-500/15 transition-colors"
                    >
                      <div className="text-center">
                        <Wand2 className="w-10 h-10 text-purple-400/60 mx-auto mb-2" />
                        <p className="text-white/70 font-medium text-sm">Generate with AI</p>
                      </div>
                    </button>
                  </div>
                </div>
              </motion.div>

              {filteredAndSortedImages.map((image, index) => (
                <div
                  key={image.id}
                  draggable
                  onDragStart={(e) => {
                    e.dataTransfer.setData('imageId', image.id)
                    e.dataTransfer.setData('action', 'createScene')
                    e.dataTransfer.effectAllowed = 'move'
                  }}
                >
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ delay: index * 0.05 }}
                  className={`group relative cursor-pointer ${
                    viewMode === 'grid' 
                      ? 'glass-card overflow-hidden hover:scale-105 transition-transform duration-200 p-0'
                      : 'glass-card flex items-center space-x-4 p-4'
                  } ${
                    selectedImageId === image.id ? 'ring-2 ring-primary-400' : ''
                  }`}
                  onClick={() => onImageSelect(image.id)}
                >
                  {viewMode === 'grid' ? (
                    <>
                      {/* Grid View - Fill entire frame without padding, use thumbnail for faster loading */}
                      <div className="relative w-full h-0" style={{ paddingBottom: '56.25%' }}>
                        <img
                          src={image.thumbnailUrl || image.url}
                          alt={image.name}
                          className="absolute inset-0 w-full h-full object-cover"
                          loading="lazy"
                        />
                      </div>

                      {/* Overlay with actions */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                        {/* Top indicators */}
                        <div className="absolute top-2 left-2 flex flex-col space-y-1">
                          <div className="flex space-x-1">
                            {image.isMain && (
                              <span className="glass-button px-2 py-1 text-xs text-yellow-300 bg-yellow-500/20">
                                Main
                              </span>
                            )}
                            {image.isFavorite && (
                              <span className="glass-button px-2 py-1 text-xs text-red-300 bg-red-500/20">
                                Favorite
                              </span>
                            )}
                          </div>
                          
                          {/* AI/Upload tag - Show on hover */}
                          {image.name.startsWith('ai_') ? (
                            <span className="glass-button px-2 py-1 text-xs text-purple-300 bg-purple-500/40 flex items-center space-x-1 shadow-lg">
                              <Wand2 className="w-3 h-3" />
                              <span>AI</span>
                            </span>
                          ) : (
                            <span className="glass-button px-2 py-1 text-xs text-green-300 bg-green-500/40 flex items-center space-x-1 shadow-lg">
                              <Upload className="w-3 h-3" />
                              <span>Uploaded</span>
                            </span>
                          )}
                        </div>

                        {/* Menu button */}
                        <button
                          onClick={(e) => handleMenuClick(e, image.id)}
                          className="absolute top-2 right-2 glass-button p-1.5 hover:bg-white/30"
                        >
                          <MoreVertical className="w-4 h-4 text-white" />
                        </button>

                        {/* Bottom actions */}
                        <div className="absolute bottom-2 left-2 right-2 flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                handleSetMainImage(image.id)
                              }}
                              className={`glass-button p-1.5 ${
                                image.isMain ? 'text-yellow-400' : 'text-white hover:text-yellow-400'
                              }`}
                              disabled={loading}
                            >
                              <Star className="w-4 h-4" fill={image.isMain ? 'currentColor' : 'none'} />
                            </button>

                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                handleToggleFavorite(image.id)
                              }}
                              className={`glass-button p-1.5 ${
                                image.isFavorite ? 'text-red-400' : 'text-white hover:text-red-400'
                              }`}
                              disabled={loading}
                            >
                              <Heart className="w-4 h-4" fill={image.isFavorite ? 'currentColor' : 'none'} />
                            </button>

                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                onImageSelect(image.id, true)
                              }}
                              className="glass-button p-1.5 text-white hover:text-blue-400 flex items-center space-x-1"
                            >
                              <MessageCircle className="w-4 h-4" />
                              <span className="text-xs">{getCommentCount(image)}</span>
                            </button>
                          </div>

                          <div className="text-xs text-white/80">
                            {image.name}
                          </div>
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      {/* List View - use thumbnail for faster loading */}
                      <div className="w-20 h-12 flex-shrink-0 bg-gray-800 rounded overflow-hidden">
                        <img
                          src={image.thumbnailUrl || image.url}
                          alt={image.name}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                      </div>

                      <div className="flex-1 min-w-0">
                        <h3 className="text-white font-medium truncate">{image.name}</h3>
                        <p className="text-white/60 text-sm">
                          {new Date(image.uploadedAt).toLocaleDateString()}
                        </p>
                      </div>

                      <div className="flex items-center space-x-2">
                        {image.isMain && (
                          <Star className="w-4 h-4 text-yellow-400" fill="currentColor" />
                        )}
                        {image.isFavorite && (
                          <Heart className="w-4 h-4 text-red-400" fill="currentColor" />
                        )}
                        <div className="flex items-center space-x-1 text-white/60">
                          <MessageCircle className="w-4 h-4" />
                          <span className="text-sm">{getCommentCount(image)}</span>
                        </div>
                      </div>

                      <button
                        onClick={(e) => handleMenuClick(e, image.id)}
                        className="glass-button p-1.5 hover:bg-white/20"
                      >
                        <MoreVertical className="w-4 h-4 text-white" />
                      </button>
                    </>
                  )}

                  {/* Dropdown Menu - Narrower, moved up 24px */}
                  {activeMenuId === image.id && (
                    <motion.div
                      className="absolute right-0 w-44 glass-card border border-white/20 z-50 shadow-xl"
                      style={{ top: '-14px', marginRight: '8px' }}
                      initial={{ opacity: 0, y: -10, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -10, scale: 0.95 }}
                      transition={{ duration: 0.15 }}
                    >
                      <div className="py-1" onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            handleSetMainImage(image.id)
                          }}
                          disabled={loading || image.isMain}
                          className="w-full px-3 py-1.5 text-left text-sm text-white hover:bg-white/10 flex items-center space-x-2 disabled:opacity-50"
                        >
                          <Star className="w-3.5 h-3.5" />
                          <span className="whitespace-nowrap">{image.isMain ? 'Main' : 'Set as Main'}</span>
                        </button>
                        
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            handleToggleFavorite(image.id)
                          }}
                          disabled={loading}
                          className="w-full px-3 py-1.5 text-left text-sm text-white hover:bg-white/10 flex items-center space-x-2 disabled:opacity-50"
                        >
                          <Heart className="w-3.5 h-3.5" />
                          <span className="whitespace-nowrap">{image.isFavorite ? 'Remove Favorite' : 'Add Favorite'}</span>
                        </button>

                        <button 
                          onClick={(e) => {
                            e.stopPropagation()
                            // Download functionality
                            const link = document.createElement('a')
                            link.href = image.url
                            link.download = image.name
                            link.target = '_blank'
                            link.click()
                          }}
                          className="w-full px-3 py-1.5 text-left text-sm text-white hover:bg-white/10 flex items-center space-x-2"
                        >
                          <Download className="w-3.5 h-3.5" />
                          <span>Download</span>
                        </button>

                        <hr className="border-white/10 my-1" />

                        <button 
                          onClick={(e) => {
                            e.stopPropagation()
                            handleOpenMoveDialog(image.id)
                          }}
                          className="w-full px-3 py-1.5 text-left text-sm text-white hover:bg-white/10 flex items-center space-x-2"
                        >
                          <ArrowRight className="w-3.5 h-3.5" />
                          <span>Move to scene</span>
                        </button>

                        <button 
                          onClick={(e) => {
                            e.stopPropagation()
                            handleOpenCreateSceneDialog(image.id)
                          }}
                          className="w-full px-3 py-1.5 text-left text-sm text-white hover:bg-white/10 flex items-center space-x-2"
                        >
                          <PlusSquare className="w-3.5 h-3.5" />
                          <span>Create a scene</span>
                        </button>

                        <hr className="border-white/10 my-1" />
                        
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            handleDeleteImage(image.id)
                          }}
                          disabled={loading}
                          className="w-full px-3 py-1.5 text-left text-sm text-red-300 hover:bg-red-500/20 flex items-center space-x-2 disabled:opacity-50"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                          <span>Delete</span>
                        </button>
                      </div>
                    </motion.div>
                  )}
                </motion.div>
                </div>
              ))}
            </AnimatePresence>
          </div>
        ) : (
          <div className="h-full flex items-center justify-center">
            <div className="text-center max-w-2xl">
              <div className="flex justify-center space-x-4 mb-8">
                <Upload className="w-16 h-16 text-green-400/40" />
                <Wand2 className="w-16 h-16 text-purple-400/40" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">No Images Yet</h3>
              <p className="text-white/70 mb-8">
                Upload your first image or generate one with AI to start building this scene
              </p>
              <div className="flex justify-center space-x-4">
                <button
                  onClick={onUploadClick}
                  className="glass-button bg-green-500/30 hover:bg-green-500/40 text-white px-6 py-3 flex items-center space-x-2"
                >
                  <Upload className="w-5 h-5" />
                  <span>Upload Images</span>
                </button>
                <button
                  onClick={onGenerateClick}
                  className="glass-button bg-purple-500/30 hover:bg-purple-500/40 text-white px-6 py-3 flex items-center space-x-2"
                >
                  <Wand2 className="w-5 h-5" />
                  <span>Generate with AI</span>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Close menu when clicking outside */}
      {activeMenuId && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setActiveMenuId(null)}
        />
      )}

      {/* Move Image Dialog */}
      {selectedScene && (
        <MoveImageDialog
          open={moveDialogOpen}
          onClose={() => {
            setMoveDialogOpen(false)
            setSelectedActionImageId(null)
          }}
          imageId={selectedActionImageId}
          currentSceneId={selectedScene.id}
        />
      )}

      {/* Create Scene Dialog (for creating new scene from image) */}
      {selectedScene && (
        <CreateSceneDialog
          open={createSceneDialogOpen}
          onClose={() => {
            setCreateSceneDialogOpen(false)
            setSelectedActionImageId(null)
          }}
          projectId={selectedScene.projectId}
          initialImageId={selectedActionImageId}
        />
      )}
    </div>
  )
}

export default ImageGallery 